import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { FuaAssessmentQuestionnaireItem } from 'components/Assessments/FuaAssessment/types/questionnaire'
import { RiskAssessmentFormField } from './formField'

/**
 * Risk Assessment subsection adapter
 * Handles complex risk assessments including ScoredChecklist components (18 fields)
 * This is one of the more complex subsections with various risk factors and safety measures
 */
export class RiskAssessmentAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hospitalization risk assessment
    const hospitalizationRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK
    )?.at(0)

    const hospitalizationRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS
    )

    const hospitalizationRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
    )?.at(0)

    // Emergency planning
    const triageCode = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE
    )?.at(0)

    const emergencyPlan = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN
    )?.at(0)

    const safeRoom = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM
    )

    // Fire safety assessment
    const fireAssessmentOxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN
    )?.at(0)

    const fireSafetyPrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS
    )

    // Homebound status
    const homeboundStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS
    )?.at(0)

    // Infection risk assessment
    const infectionRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK
    )?.at(0)

    const infectionRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS
    )

    const infectionRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER
    )?.at(0)

    // Fall risk assessment (ScoredChecklist component)
    const fallRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK
    )?.at(0)

    const fallRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS
    )

    const fallRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER
    )?.at(0)

    const fallRiskAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT
    )

    // Financial and environmental safety
    const financeRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK
    )?.at(0)

    const financeRiskOther = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER
    )?.at(0)

    const environmentSafety = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY
    )

    const nurseSafetyRisks = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS
    )

    const safetyMeasures = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES
    )

    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES
    )?.at(0)

    return {
      [RiskAssessmentFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK]: hospitalizationRisk,
      [RiskAssessmentFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS]:
        hospitalizationRiskFactors,
      [RiskAssessmentFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER]:
        hospitalizationRiskFactorsOther,
      [RiskAssessmentFormField.RISK_ASSESSMENT_TRIAGE_CODE]: triageCode,
      [RiskAssessmentFormField.RISK_ASSESSMENT_EMERGENCY_PLAN]: emergencyPlan,
      [RiskAssessmentFormField.RISK_ASSESSMENT_SAFE_ROOM]: safeRoom,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN]: fireAssessmentOxygen,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS]: fireSafetyPrecautions,
      [RiskAssessmentFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS]: homeboundStatus,
      [RiskAssessmentFormField.RISK_ASSESSMENT_INFECTION_RISK]: infectionRisk,
      [RiskAssessmentFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS]: infectionRiskFactors,
      [RiskAssessmentFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER]:
        infectionRiskFactorsOther,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK]: fallRisk,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS]: fallRiskFactors,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER]: fallRiskFactorsOther,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT]: fallRiskAssessment,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FINANCE_RISK]: financeRisk,
      [RiskAssessmentFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER]: financeRiskOther,
      [RiskAssessmentFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY]: environmentSafety,
      [RiskAssessmentFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS]: nurseSafetyRisks,
      [RiskAssessmentFormField.RISK_ASSESSMENT_SAFETY_MEASURES]: safetyMeasures,
      [RiskAssessmentFormField.RISK_ASSESSMENT_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK)
      },
      {
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS,
        value: this.getFormDataField(
          RiskAssessmentFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS
        )
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_TRIAGE_CODE)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_EMERGENCY_PLAN)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_SAFE_ROOM)
      },
      {
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_INFECTION_RISK)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS)
      },
      {
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_FINANCE_RISK)
      },
      {
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY)
      },
      {
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_SAFETY_MEASURES)
      },
      {
        linkId: FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_NOTES)
      }
    ]

    if (this.formDataFieldExists(RiskAssessmentFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS)) {
      mutations.push({
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS,
        value: this.getFormDataField(
          RiskAssessmentFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS
        )
      })
    }

    if (this.formDataFieldExists(RiskAssessmentFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)) {
      mutations.push({
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)
      })
    }
    if (
      this.formDataFieldExists(RiskAssessmentFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER)
    ) {
      mutations.push({
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER,
        value: this.getFormDataField(
          RiskAssessmentFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER
        )
      })
    }
    if (this.formDataFieldExists(RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER,
        value: this.getFormDataField(
          RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER
        )
      })
    }
    if (this.formDataFieldExists(RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)) {
      mutations.push({
        linkId:
          FuaAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT,
        value: this.getFormDataField(RiskAssessmentFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)
      })
    }

    return mutations
  }
}
